<script setup>
import { onMounted, computed, ref, watch } from 'vue';
import { useMediaQuery } from '@vueuse/core';
import { useContactDetailI18n } from 'dashboard/components-next/Contacts/useContactDetailI18n';
import ConversationAPI from 'dashboard/api/conversations';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';

import ContactsDetailsLayout from 'dashboard/components-next/Contacts/ContactsDetailsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Policy from 'dashboard/components/policy.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ContactDetails from 'dashboard/components-next/Contacts/Pages/ContactDetails.vue';
import ContactNotes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactNotes.vue';
import AceleresStartConversationFlow from 'dashboard/components/AceleresStartConversationFlow.vue';
import ContactHistory from 'dashboard/components-next/Contacts/ContactsSidebar/ContactHistory.vue';
import ContactMergeDialog from 'dashboard/components-next/Contacts/ContactsForm/ContactMergeDialog.vue';
import { isAceleresGroupContact } from 'dashboard/helper/aceleresGroupContact';
import ContactCustomAttributes from 'dashboard/components-next/Contacts/ContactsSidebar/ContactCustomAttributes.vue';

const store = useStore();
const route = useRoute();
const router = useRouter();

const contact = useMapGetter('contacts/getContactById');
const uiFlags = useMapGetter('contacts/getUIFlags');

const activeTab = ref('data');
const isMobileViewport = useMediaQuery('(max-width: 767px)');
watch(isMobileViewport, isMobile => {
  if (!isMobile && activeTab.value === 'attributes') activeTab.value = 'data';
});
const contactDetailsRef = ref(null);
const contactMergeRef = ref(null);
const startConversationFlowRef = ref(null);
const startingConversation = ref(false);

const isFetchingItem = computed(() => uiFlags.value.isFetchingItem);
const isMergingContact = computed(() => uiFlags.value.isMerging);
const isUpdatingContact = computed(() => uiFlags.value.isUpdating);

const selectedContact = computed(() => contact.value(route.params.contactId));

const showSpinner = computed(
  () => isFetchingItem.value || isMergingContact.value
);

const { t } = useContactDetailI18n();

const CONTACT_TABS_OPTIONS = [
  { key: 'DATA', value: 'data' },
  { key: 'ATTRIBUTES', value: 'attributes' },
  { key: 'HISTORY', value: 'history' },
  { key: 'NOTES', value: 'notes' },
];

// A entrada deste contato na store perde nome, identifier e atributos depois
// de carregada: uma listagem dispara CLEAR_CONTACTS + SET_CONTACTS sem ele, e
// só `{ id, contact_inboxes }` volta (medido em 14/09/2026). Guardar o último contato completo
// desta rota evita que a aba Mesclar reapareça para grupo e que o modal de
// mesclar mostre o contato em branco.
const lastLoadedContact = ref(null);
watch(
  () => [route.params.contactId, selectedContact.value],
  ([contactId, current]) => {
    if (String(lastLoadedContact.value?.id) !== String(contactId)) {
      lastLoadedContact.value = null;
    }
    if (current?.name !== undefined) lastLoadedContact.value = current;
  },
  { immediate: true }
);
const stableContact = computed(
  () => lastLoadedContact.value || selectedContact.value
);

const notes = useMapGetter('contactNotes/getAllNotesByContactId');
const conversations = useMapGetter(
  'contactConversations/getAllConversationsByContactId'
);
const tabCounts = computed(() => ({
  notes: notes.value(route.params.contactId).length,
  history: conversations.value(route.params.contactId).length,
}));

const openContactConversation = async identity => {
  if (startingConversation.value) return;
  const connection = identity?.connections?.[0];
  if (!connection) return;
  const contactId = connection.contact_id || route.params.contactId;
  const existing = identity.conversation;
  if (existing) {
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: route.params.accountId,
        conversation_id: existing.id,
      },
    });
    return;
  }
  if (identity.channel === 'whatsapp' && identity.kind === 'phone') {
    startingConversation.value = true;
    try {
      if (!store.getters['inboxes/getInboxes']?.length)
        await store.dispatch('inboxes/get');
      await startConversationFlowRef.value?.startForPhone(
        identity.address,
        connection.inbox_id,
        contactId
      );
    } catch {
      useAlert(t('CONTACT_DETAIL.CONVERSATION_ERROR'));
    } finally {
      startingConversation.value = false;
    }
    return;
  }
  startingConversation.value = true;
  try {
    const { data: conversation } = await ConversationAPI.create({
      contact_id: contactId,
      inbox_id: connection.inbox_id,
      source_id: identity.source_id,
    });
    router.push({
      name: 'inbox_conversation',
      params: {
        accountId: route.params.accountId,
        conversation_id: conversation.id,
      },
    });
  } catch {
    useAlert(t('CONTACT_DETAIL.CONVERSATION_ERROR'));
  } finally {
    startingConversation.value = false;
  }
};

const tabs = computed(() => {
  return CONTACT_TABS_OPTIONS.filter(
    tab => isMobileViewport.value || tab.value !== 'attributes'
  ).map(tab => ({
    label:
      tab.value === 'data'
        ? t('CONTACT_DETAIL_VIEW.DATA')
        : t(`CONTACTS_LAYOUT.SIDEBAR.TABS.${tab.key}`),
    value: tab.value,
  }));
});

const goToContactsList = () => {
  if (window.history.state?.back || window.history.length > 1) {
    router.back();
  } else {
    router.push(`/app/accounts/${route.params.accountId}/contacts?page=1`);
  }
};

const fetchActiveContact = async () => {
  if (!route.params.contactId) return false;

  await store.dispatch('contacts/show', { id: route.params.contactId });
  if (!selectedContact.value) {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE')
    );
    goToContactsList();
    return false;
  }

  try {
    await store.dispatch(
      'contacts/fetchContactableInbox',
      route.params.contactId
    );
    return true;
  } catch {
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE')
    );
    goToContactsList();
    return false;
  }
};

const handleTabChange = tab => {
  activeTab.value = tab.value;
};

const fetchContactNotes = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactNotes/get', { contactId });
};

const fetchContactConversations = () => {
  const { contactId } = route.params;
  if (contactId) store.dispatch('contactConversations/get', contactId);
};

const fetchAttributes = () => {
  store.dispatch('attributes/get');
};

const toggleContactBlock = async isBlocked => {
  const ALERT_MESSAGES = {
    success: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_SUCCESS_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_SUCCESS_MESSAGE'),
    },
    error: {
      block: t('CONTACTS_LAYOUT.HEADER.ACTIONS.BLOCK_ERROR_MESSAGE'),
      unblock: t('CONTACTS_LAYOUT.HEADER.ACTIONS.UNBLOCK_ERROR_MESSAGE'),
    },
  };

  try {
    await store.dispatch(`contacts/update`, {
      ...selectedContact.value,
      blocked: !isBlocked,
    });
    useAlert(
      isBlocked ? ALERT_MESSAGES.success.unblock : ALERT_MESSAGES.success.block
    );
  } catch (error) {
    useAlert(
      isBlocked ? ALERT_MESSAGES.error.unblock : ALERT_MESSAGES.error.block
    );
  }
};

onMounted(async () => {
  const isVisible = await fetchActiveContact();
  if (!isVisible) return;

  fetchContactNotes();
  fetchContactConversations();
  fetchAttributes();
});
</script>

<template>
  <ContactsDetailsLayout
    :selected-contact="stableContact"
    :is-updating="isUpdatingContact"
    @go-to-contacts-list="goToContactsList"
    @toggle-block="toggleContactBlock"
  >
    <template #actions>
      <Button
        v-if="!isAceleresGroupContact(stableContact)"
        :label="t('CONTACTS_LAYOUT.SIDEBAR.TABS.MERGE')"
        icon="i-lucide-git-merge"
        justify="start"
        size="sm"
        slate
        ghost
        class="w-full"
        @click="contactMergeRef?.open()"
      />
      <Policy :permissions="['administrator']">
        <Button
          :label="t('CONTACTS_LAYOUT.DETAILS.DELETE_CONTACT')"
          icon="i-lucide-trash-2"
          justify="start"
          size="sm"
          ruby
          ghost
          class="w-full"
          @click="contactDetailsRef?.openConfirmDeleteContactDialog()"
        />
      </Policy>
    </template>
    <template #tabs>
      <button
        v-for="tab in tabs"
        :key="tab.value"
        type="button"
        class="shrink-0 border-b-2 px-0.5 pb-2.5 pt-1 text-sm max-md:min-w-0 max-md:px-0 max-md:text-xs"
        :class="
          activeTab === tab.value
            ? 'border-[#202939] font-semibold text-[#202939] max-md:!border-[#16a34a]'
            : 'border-transparent text-n-slate-10 hover:text-n-slate-12'
        "
        :aria-current="activeTab === tab.value ? 'page' : undefined"
        @click="handleTabChange(tab)"
      >
        {{ tab.label
        }}<span
          v-if="tabCounts[tab.value]"
          class="ml-2 inline-flex items-center justify-center text-xs max-md:hidden"
          :class="
            activeTab === tab.value
              ? 'min-w-5 rounded-full bg-[#111827] px-1.5 text-white'
              : 'font-normal text-n-slate-10'
          "
          >{{ tabCounts[tab.value] }}</span
        >
      </button>
    </template>
    <div v-if="showSpinner" class="flex justify-center py-10"><Spinner /></div>
    <template v-else-if="stableContact">
      <ContactDetails
        ref="contactDetailsRef"
        :selected-contact="stableContact"
        :show-data="activeTab === 'data'"
        @go-to-contacts-list="goToContactsList"
        @open-conversation="openContactConversation"
      />
      <section
        v-show="isMobileViewport ? activeTab === 'attributes' : activeTab === 'data'"
        class="rounded-2xl bg-white p-4 shadow-[0_8px_24px_rgba(0,0,0,0.04)]"
      >
        <h2 class="mb-4 mt-0 text-sm font-semibold text-n-slate-12">
          {{ t('CONTACTS_LAYOUT.SIDEBAR.TABS.ATTRIBUTES') }}
        </h2>
        <ContactCustomAttributes :selected-contact="stableContact" />
      </section>
      <div
        v-if="activeTab === 'notes' || activeTab === 'history'"
        :class="{
          'rounded-[20px] bg-white p-5 shadow-[0_12px_32px_rgba(0,0,0,0.05)]':
            activeTab === 'history',
        }"
      >
        <ContactNotes v-if="activeTab === 'notes'" />
        <ContactHistory v-if="activeTab === 'history'" />
      </div>
    </template>
    <ContactMergeDialog
      v-if="stableContact && !isAceleresGroupContact(stableContact)"
      ref="contactMergeRef"
      :selected-contact="stableContact"
      @merged="goToContactsList"
    />
    <AceleresStartConversationFlow
      ref="startConversationFlowRef"
      :account-id="route.params.accountId"
    />
  </ContactsDetailsLayout>
</template>
